/**
 * This module provides the BatterySettings higher order component.
 *
 * It defines the layout of the settings sections.
 *
 * And it prepares the consumed form values. The injected form values will be updated as soon as an update request was
 * initiated. This is required to trigger the reactivity behaviour of Formik later on.
 *
 */

import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import WallboxSettingsForm from 'src/Libs/wallboxes/components/settings/WallboxSettingsForm'
import settingsModelToSettingsFormTransformer from 'src/Libs/wallboxes/transformers/wallbox-common-settings/settings-model-to-settings-form.transformer'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { WallboxType } from 'src/Libs/wallboxes/api'
import { makeStyles } from 'tss-react/mui'
import FormikWithExitHandler from 'src/Libs/form/components/FormikWithExitHandler'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import LeaveSunModeEnabledDialog from './sun-mode/LeaveSunModeEnabledDialog'
import { ActionPositions, UpToThreeActions } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { useUserSettings } from 'src/Hooks'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { WallboxOcppModel } from '../models'
import { WallboxSettingsFormModel } from '../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import { useWallboxSettingsFormInitializer } from '../hooks/validations'
import { useFarmContext } from 'src/Elements/Farm'

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

interface Props {
  model: WallboxConfigurationModel | undefined
  ocppSettings: WallboxOcppModel | null
  ocppError: Error | null
  onUpdate: (values: WallboxSettingsFormModel, oldValues: WallboxSettingsFormModel) => Promise<void>
  isReadOnlyAccess?: boolean
}

const WallboxSettings: React.FC<Props> = ({ model, ocppSettings, ocppError, onUpdate, isReadOnlyAccess = true }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { userSettings, updateUserSettings } = useUserSettings()
  const { initialValues, validationSchema } = useWallboxSettingsFormInitializer(model?.wallboxType || WallboxType.DefaultE3dc, ocppSettings, model)
  const { isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)

  const { storage } = useActiveInstallationContext()
  const softwareVersion = storage?.softwareRelease

  const [formValues, setFormValues] = useState<WallboxSettingsFormModel>(initialValues)

  useEffect(() => {
    setFormValues(settingsModelToSettingsFormTransformer(model, ocppSettings))
  }, [ocppSettings, model])

  const [isSunModeDialogOpen, setIsSunModeDialogOpen] = useState(false)

  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true
  const { triggerAppRating } = useAppRatingContext()

  const dialogActions: UpToThreeActions = [
    {
      name: t('systemSettings.wallbox.settings.leave-sunmode-enabled-dialog.actions.not-show-again'),
      onClick: async (): Promise<void> => {
        await updateUserSettings({ dontShowLeaveSunModeEnabledDialogAgain: true })
        handleFormUpdate(formValues)
        setIsSunModeDialogOpen(false)
      },
      style: 'secondary',
      position: ActionPositions.One,
    },
    {
      name: t('systemSettings.wallbox.settings.leave-sunmode-enabled-dialog.actions.deactivate'),
      onClick: (): void => {
        handleFormUpdate({ ...formValues, sunModeActive: false })
        setIsSunModeDialogOpen(false)
      },
      style: 'secondary',
      position: ActionPositions.Three,
    },
    {
      name: t('systemSettings.wallbox.settings.leave-sunmode-enabled-dialog.actions.leave-activated'),
      onClick: (): void => {
        handleFormUpdate({ ...formValues, sunModeActive: true })
        setIsSunModeDialogOpen(false)
      },
      style: 'primary',
      position: ActionPositions.Four,
    },
  ]

  const handleFormUpdate = useCallback(
    async (values: WallboxSettingsFormModel): Promise<void> => {
      try {
        setFormValues({ ...values, ocppPassword: undefined })
        await onUpdate(values, formValues)
        triggerAppRating()
      } catch (error) {
        /* revert changes */
        setFormValues({ ...formValues })
      }
    },
    [onUpdate, formValues, triggerAppRating]
  )

  const handleSubmit = useCallback(
    async (values: WallboxSettingsFormModel, initialValues?: WallboxSettingsFormModel) => {
      const isSunModeDeactivated = initialValues?.sunModeActive === true && values.sunModeActive === false
      if (
        isDynamicPowerPriceEnabledForWallboxes &&
        isSunModeDeactivated &&
        model?.wallboxType !== WallboxType.Bidi &&
        userSettings.dontShowLeaveSunModeEnabledDialogAgain !== true
      ) {
        setIsSunModeDialogOpen(true)
      } else {
        await handleFormUpdate(values)
      }
    },
    [handleFormUpdate, isDynamicPowerPriceEnabledForWallboxes, model?.wallboxType, userSettings.dontShowLeaveSunModeEnabledDialogAgain]
  )

  return (
    <OptionalTooltipWrapper hasTooltip={isServerOffline} placement={'top'} titleKey={'systemSettings.wallbox.settings.master-offline'}>
      <AccordionItem
        expanded={!isServerOffline}
        disabled={isServerOffline}
        title={t('systemSettings.wallbox.settings.sectionTitle')}
        contentClassName={classes.contentAccordion}
        headerContentClassName={classes.headerContent}
      >
        {model ? (
          <FormikWithExitHandler
            formKey="wallbox-settings-form"
            initialValues={formValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <>
              <LeaveSunModeEnabledDialog
                isOpen={isSunModeDialogOpen}
                handleClose={() => setIsSunModeDialogOpen(false)}
                dialogActions={dialogActions}
              />
              <WallboxSettingsForm
                model={model}
                ocppSettings={ocppSettings}
                ocppError={ocppError}
                hasWritingAccess={!isReadOnlyAccess}
                softwareVersion={softwareVersion}
              />
            </>
          </FormikWithExitHandler>
        ) : (
          <SystemSettingsBlankView />
        )}
      </AccordionItem>
    </OptionalTooltipWrapper>
  )
}

export default WallboxSettings
